export type DataTableColumnPinGroup = 'left' | 'center' | 'right';

export interface ColumnWindowColumn {
	id: string;
	pinGroup: DataTableColumnPinGroup;
	width: number;
}

/**
 * Overscan applied on each side of the viewport, in pixels. A generous buffer
 * keeps a band of columns mounted just outside the viewport so fast horizontal
 * scrolling does not outrun the rendered window and flash empty tracks.
 */
export const DEFAULT_COLUMN_WINDOW_OVERSCAN_PX = 600;

/**
 * Returns the ids of the center (non-pinned) columns whose horizontal track
 * range intersects the scrolled viewport, expanded by `overscanPx` on each side.
 *
 * Columns are laid out left-to-right in array order; each column's track starts
 * at the cumulative width of the columns before it. Widths come from the frozen
 * column-sizing map so the window stays aligned with the rendered CSS grid
 * tracks. Pinned columns are never included — callers always render them, so
 * their sticky positioning and offsets are unaffected.
 */
export function computeRenderedCenterColumns(
	columns: ColumnWindowColumn[],
	scrollLeft: number,
	viewportWidth: number,
	overscanPx: number = DEFAULT_COLUMN_WINDOW_OVERSCAN_PX,
): Set<string> {
	const viewStart = scrollLeft - overscanPx;
	const viewEnd = scrollLeft + viewportWidth + overscanPx;

	const rendered = new Set<string>();
	let offset = 0;

	for (const column of columns) {
		const start = offset;
		const end = offset + column.width;
		offset = end;

		if (column.pinGroup !== 'center') continue;
		if (end > viewStart && start < viewEnd) rendered.add(column.id);
	}

	return rendered;
}
